import { useEffect, useState } from 'react';
import { api } from '../api';
import { ApiariesCard } from '../components/ApiariesCard';
import { ErrorNotice, Spinner, StatusPill, fullName } from '../components/ui';
import type { Member } from '../types';

/**
 * Every signed-in member's own page, whether or not they hold a single
 * committee permission. Before this page existed, an ordinary member with no
 * committee_area had no way to see their own record at all — /members/{id}
 * sits behind members_view. This reads through GET /members/me instead,
 * which exists for exactly this.
 */
export function MyAccountPage() {
  const [member, setMember] = useState<Member | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<{ member: Member }>('/members/me')
      .then((result) => setMember(result.member))
      .catch(setError);
  }, []);

  if (member === null && error === null) return <Spinner label="Loading your account" />;

  return (
    <>
      <div className="page-head">
        <h1>My account</h1>
      </div>

      <ErrorNotice error={error} />

      {member !== null ? (
        <>
          <div className="card">
            <dl className="details">
              <div className="detail">
                <dt>Name</dt>
                <dd>{fullName(member)}</dd>
              </div>
              <div className="detail">
                <dt>Status</dt>
                <dd>
                  <StatusPill status={member.status} />
                </dd>
              </div>
              <div className="detail">
                <dt>Email</dt>
                <dd>{member.email ?? '—'}</dd>
              </div>
            </dl>
            <p className="muted small">
              Spotted something wrong here? Get in touch with the committee to have it corrected.
            </p>
          </div>

          {/* Self-service: goes through /apiaries/me and needs no permission. */}
          <ApiariesCard memberId={member.id} mode="self" />

          <InvitePartnerCard />
        </>
      ) : null}
    </>
  );
}

function InvitePartnerCard() {
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function generate() {
    setError(null);
    setBusy(true);
    try {
      const invite = await api.post<{ token: string }>('/members/me/partner-invites');
      setLink(`${window.location.origin}/join?invite=${invite.token}`);
      setCopied(false);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (link === null) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    } catch {
      // Clipboard access can fail quietly (permissions, insecure context).
      // The link is still shown and selectable, so nothing is lost.
    }
  }

  return (
    <div className="card">
      <h2>Invite a partner member</h2>
      <p className="muted small">
        If someone in your household — a spouse or partner — wants to join as a Partner Member,
        send them this link. It works once and expires in 7 days. This is the only way a Partner
        Member application gets linked to your account.
      </p>

      <ErrorNotice error={error} />

      {link === null ? (
        <button type="button" disabled={busy} onClick={() => void generate()}>
          {busy ? 'Generating…' : 'Generate an invite link'}
        </button>
      ) : (
        <div className="invite-link">
          <input type="text" readOnly value={link} onFocus={(e) => e.target.select()} />
          <button type="button" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
    </div>
  );
}
